import { observer } from 'mobx-react'
import { GetStaticProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useEffect, useState } from 'react'
import { PrimaryHeading } from '@/components/atoms/PrimaryHeading'
import { MyHead } from '@/components/molecules/MyHead'
import { VerticalNavDefaultPageTemplate } from '@/components/templates/VerticalNavDefaultPageTemplate'
import { ProfileSingleColumnContentTemplate } from '@/components/templates/ProfileSingleColumnContentTemplate'
import { getStaticStores, useStores } from '@/utils'
import { CurrencyUnit, IJobBase, JobTypeOfPosition, JobWorkExperience, JobWorkingForm, Language } from '@/types'
import { Button } from '@/components/atoms/Button'
import { JobsNavbar } from '@/components/organisms/JobsNavbar'
import { FetchJobsValues, KeywordSearchGroupForJobs } from '@/components/organisms/KeywordSearchGroupForJobs'
import { JobCardList } from '@/components/organisms/JobCardList'
import { ProfileCompletionPromotionPopup } from '@/components/organisms/ProfileCompletionPromotionPopup'
import styles from './index.module.scss'

type Props = {
  staticJobs: IJobBase[]
  staticHasNextJobsPage: boolean
}

const NUM_OF_ITEMS = 10

const Jobs: NextPage<Props> = observer(({ staticJobs, staticHasNextJobsPage }) => {
  const { t } = useTranslation(['jobs'])
  const { jobs } = useStores()
  const [searchResults, setSearchResults] = useState<IJobBase[]>(jobs.jobs.length > 0 ? jobs.jobs : staticJobs)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [currencyUnit, setCurrencyUnit] = useState<CurrencyUnit>(null)
  const [minMonthlySalary, setMinMonthlySalary] = useState<string>(null)
  const [maxMonthlySalary, setMaxMonthlySalary] = useState<string>(null)
  const [tokenGrant, setTokenGrant] = useState<boolean[]>([])
  const [typeOfPosition, setTypeOfPosition] = useState<JobTypeOfPosition[]>([])
  const [workExperience, setWorkExperience] = useState<JobWorkExperience>(null)
  const [workingForm, setWorkingForm] = useState<JobWorkingForm[]>([])
  const [jobCategoryIds, setJobCategoryIds] = useState<string[]>([])
  const [skillIds, setSkillIds] = useState<string[]>([])
  const [locationIds, setLocationIds] = useState<string[]>([])
  const [isFetching, setIsFetching] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const searchHandler = async (input: FetchJobsValues) => {
    const result = await jobs.fetchJobs({
      searchWord: input.keyword,
      currencyUnit: input.unit,
      minMonthlySalary: input.minSalary,
      maxMonthlySalary: input.maxSalary,
      tokenGrant: input.token,
      typeOfPosition: input.position,
      workExperience: input.experience,
      workingForm: input.form,
      jobCategoryIds: input.jobCategories,
      skillIds: input.skills,
      locationIds: input.locations,
      limit: NUM_OF_ITEMS,
      shouldRefresh: true,
    })
    setSearchResults(result)
  }

  const fetchJobs = async () => {
    setIsFetching(true)
    const isFirstTimeFetching = staticJobs === searchResults
    await jobs.fetchJobs({
      searchWord: searchKeyword,
      currencyUnit,
      minMonthlySalary,
      maxMonthlySalary,
      tokenGrant,
      typeOfPosition,
      workExperience,
      workingForm,
      jobCategoryIds,
      skillIds,
      locationIds,
      limit: isFirstTimeFetching ? NUM_OF_ITEMS * 2 : NUM_OF_ITEMS, // 初回の追加読み込みは ISR 分も含めて倍読み込む
      shouldRefresh: isFirstTimeFetching, // 初回の追加読み込みは Store の中身をリセットするために shouldRefresh: true
    })
    setSearchResults(jobs.jobs)
    setIsFetching(false)
  }

  const setSearchValues = async (input: FetchJobsValues) => {
    setSearchKeyword(input.keyword)
    setCurrencyUnit(input.unit)
    setMinMonthlySalary(input.minSalary)
    setMaxMonthlySalary(input.maxSalary)
    setTokenGrant(input.token)
    setTypeOfPosition(input.position)
    setWorkExperience(input.experience)
    setWorkingForm(input.form)
    setJobCategoryIds(input.jobCategories)
    setSkillIds(input.skills)
    setLocationIds(input.locations)
    await searchHandler(input)
  }

  // ブラウザ側での初期化時に Store に hasNextPage を設定
  useEffect(() => {
    jobs.updateHasNextJobsPage(staticHasNextJobsPage)
  }, [])

  return (
    <VerticalNavDefaultPageTemplate>
      <MyHead
        title={t('日本中・世界中のスタートアップの求人一覧')}
        description={t(
          'スタートアップ・ベンチャーの就職、転職、副業の求人一覧です。CTOなどの役員・幹部クラスの求人も揃っています。'
        )}
      />
      <ProfileSingleColumnContentTemplate>
        <div className={styles.heading}>
          <PrimaryHeading backgroundText='JOBS'>{t('求人一覧')}</PrimaryHeading>
        </div>
        <JobsNavbar />
        <KeywordSearchGroupForJobs search={async (input: FetchJobsValues) => setSearchValues(input)} />
        <div className={styles.content}>
          <JobCardList items={searchResults} />
        </div>
        {mounted && jobs.hasNextJobsPage && (
          <div className={styles.button}>
            <Button
              styleType='default'
              componentType='button'
              buttonSize='large'
              isLoading={isFetching}
              onClick={() => fetchJobs()}
            >
              {t('+ Load More')}
            </Button>
          </div>
        )}
        <ProfileCompletionPromotionPopup type='jobSeeker' />
      </ProfileSingleColumnContentTemplate>
    </VerticalNavDefaultPageTemplate>
  )
})

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const { viewer, jobs } = getStaticStores()
  await viewer.changeLanguage(locale as Language)
  const staticJobs = await jobs.fetchJobs({ limit: NUM_OF_ITEMS, shouldRefresh: true })

  return {
    props: {
      staticJobs,
      staticHasNextJobsPage: jobs.hasNextJobsPage,
      ...(await serverSideTranslations(locale, [
        'global',
        'jobs',
        'companies-[id]',
        'angels-vcs-startups',
        'search',
        'p',
      ])),
    },
    revalidate: 1,
  }
}

export default Jobs
